import { useEffect, useRef, useState } from "react";

export function CreatePairDialog({ suggestedName, labels, count, includesBilateralDeals = false, error, onCreate, onClose }: {
  suggestedName: string;
  labels: string[];
  count: number;
  includesBilateralDeals?: boolean;
  error: string | null;
  onCreate: (name: string) => Promise<void>;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const submitting = useRef(false);
  const [name, setName] = useState(suggestedName);
  const [busy, setBusy] = useState(false);
  const trimmed = name.trim();
  const exists = labels.some(label => label.toLowerCase() === trimmed.toLowerCase());
  const close = () => {
    // Close while attached so the browser can restore focus to the opener.
    dialog.current?.close();
    onClose();
  };

  useEffect(() => {
    const element = dialog.current!;
    element.showModal();
    input.current?.select();
    return () => element.close();
  }, []);

  return (
    <dialog ref={dialog} aria-labelledby="create-pair-title" aria-describedby="create-pair-description"
      onCancel={event => { event.preventDefault(); if (!submitting.current) close(); }}
      className="m-auto w-[calc(100%_-_2rem)] max-w-md rounded-lg border border-border bg-bg p-5 text-fg shadow-xl backdrop:bg-black/45">
      <form onSubmit={async event => {
        event.preventDefault();
        if (submitting.current || !trimmed || exists || trimmed.length > 80) return;
        submitting.current = true;
        setBusy(true);
        try { await onCreate(trimmed); }
        finally { submitting.current = false; setBusy(false); }
      }} className="space-y-4">
        <h2 id="create-pair-title" className="text-base font-semibold">Create new pair</h2>
        <p id="create-pair-description" className="text-sm text-muted">
          Assign {count} selected {count === 1 ? "trade" : "trades"} to this pair. This replaces any existing pair assignments.
          {includesBilateralDeals && " Bilateral deals are updated in full, including their unselected rows."}
        </p>
        <label className="block space-y-1 text-sm">
          <span>Pair name</span>
          <input ref={input} required maxLength={80} value={name} disabled={busy}
            onChange={event => setName(event.target.value)} aria-invalid={exists}
            aria-describedby={exists ? "create-pair-name-error" : undefined}
            className="w-full rounded border border-border bg-transparent px-3 py-2 disabled:opacity-50" />
        </label>
        {exists && <p id="create-pair-name-error" role="alert" className="text-sm text-negRed">This pair already exists. Choose another name or use Assign pair.</p>}
        {error && <p role="alert" className="text-sm text-negRed">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" disabled={busy} onClick={close} className="rounded border border-border px-3 py-2 text-sm disabled:opacity-50">Cancel</button>
          <button type="submit" disabled={busy || !trimmed || exists} className="rounded bg-accent px-3 py-2 text-sm text-white disabled:opacity-50">
            {busy ? "Creating…" : "Create pair"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
